/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useEffect, useMemo, useState } from 'react';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  PostHogEventName,
  usePostHogTracking,
} from '@/hooks/usePostHogTracking';
import { createSupabaseClient } from '@/integrations/supabase/client';
import { ToolData, ToolVersion } from '@/types/toolVersion';
import { AVAILABLE_TOOLS } from '@/types/tools';

export const MAX_VERSION_NOTE_LENGTH = 1000;

interface UseToolVersionProps {
  toolId: string;
  productId?: string;
  versionId?: string;
  toolLinkId?: string;
  versionName?: string;
}

export const useToolVersion = ({
  toolId,
  productId,
  versionId,
  toolLinkId,
  versionName,
}: UseToolVersionProps) => {
  const { user, getToken, isLoaded } = useAuth();
  const { toast } = useToast();
  const { trackEvent } = usePostHogTracking();
  const supabase = useMemo(() => createSupabaseClient(getToken), [getToken]);
  const [currentVersion, setCurrentVersion] = useState<ToolVersion | null>(
    null
  );
  const [versions, setVersions] = useState<ToolVersion[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchVersions = async () => {
    if (!toolId) return;

    if (!isLoaded) {
      console.log('useToolVersion: Clerk not loaded yet, skipping fetch');
      return;
    }

    if (!user) {
      console.log('useToolVersion: No user found, cannot fetch versions');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let query = supabase
        .from('tool_versions')
        .select(
          'id, tool_id, version_name, version_note, tool_data, user_id, created_at, updated_at, product_tool_link_id, original_product_id'
        )
        .eq('tool_id', toolId)
        .order('updated_at', { ascending: false });

      if (productId && toolLinkId) {
        query = query.eq('product_tool_link_id', toolLinkId);
      }

      const { data: toolVersions, error } = await query;

      if (error) throw error;

      setVersions(toolVersions || []);

      if (versionId) {
        const specificVersion = toolVersions?.find((v) => v.id === versionId);
        setCurrentVersion(specificVersion || null);
      } else if (versionName && toolVersions) {
        const normalizedVersionName = versionName
          .replace(/-/g, ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        console.log('Looking for version with name:', normalizedVersionName);
        console.log(
          'Available versions:',
          toolVersions.map((v) => ({ id: v.id, name: v.version_name }))
        );

        let specificVersion = toolVersions.find(
          (v) => v.version_name === normalizedVersionName
        );

        if (!specificVersion) {
          specificVersion = toolVersions.find(
            (v) =>
              v.version_name.toLowerCase() ===
              normalizedVersionName.toLowerCase()
          );
        }

        if (!specificVersion) {
          specificVersion = toolVersions.find(
            (v) =>
              v.version_name
                .toLowerCase()
                .includes(normalizedVersionName.toLowerCase()) ||
              normalizedVersionName
                .toLowerCase()
                .includes(v.version_name.toLowerCase())
          );
        }

        if (!specificVersion && toolVersions.length > 0) {
          console.log('No version match found, falling back to latest version');
          specificVersion = toolVersions[0];
        }

        console.log('Selected version:', specificVersion);
        setCurrentVersion(specificVersion || null);
      } else if (toolVersions && toolVersions.length > 0) {
        setCurrentVersion(toolVersions[0]);
      } else {
        setCurrentVersion(null);
      }
    } catch (error) {
      console.error('Error fetching tool versions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tool versions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveVersion = async (
    versionId: string,
    data: ToolData,
    versionNote?: string
  ) => {
    if (!user) {
      console.error('[useToolVersion] Cannot save: No user found');
      return;
    }

    console.log('[useToolVersion] Attempting to save version:', {
      versionId,
      userId: user.id,
      toolId,
      productId,
      toolLinkId,
      dataPreview: JSON.stringify(data).substring(0, 100),
      hasVersionNote: !!versionNote,
    });

    try {
      const token = await getToken();
      console.log('[useToolVersion] Auth token available:', !!token);

      const updateData: {
        tool_data: ToolData;
        updated_at: string;
        version_note?: string;
      } = {
        tool_data: data,
        updated_at: new Date().toISOString(),
      };

      if (versionNote !== undefined) {
        // Validate version note length before saving
        if (versionNote.length > MAX_VERSION_NOTE_LENGTH) {
          const errorMessage = `Version note exceeds maximum length of ${MAX_VERSION_NOTE_LENGTH} characters. Current length: ${versionNote.length}`;
          console.error('[useToolVersion] Validation failed:', errorMessage);
          throw new Error(errorMessage);
        }
        updateData.version_note = versionNote;
      }

      const { error, data: updateResult } = await supabase
        .from('tool_versions')
        .update(updateData)
        .eq('id', versionId)
        .select();

      if (error) {
        console.error('[useToolVersion] Save failed with error:', {
          error,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        throw error;
      }

      if (!updateResult || updateResult.length === 0) {
        console.error('[useToolVersion] Save failed: No rows updated', {
          versionId,
          userId: user.id,
          productId,
          toolLinkId,
        });
        throw new Error('No rows updated - possible RLS policy block');
      }

      console.log('[useToolVersion] Version saved successfully:', {
        updatedRows: updateResult.length,
        versionId,
      });

      setCurrentVersion((prev) =>
        prev
          ? {
              ...prev,
              tool_data: data,
              ...(versionNote !== undefined && { version_note: versionNote }),
            }
          : null
      );
      setVersions((prev) =>
        prev.map((v) =>
          v.id === versionId
            ? {
                ...v,
                tool_data: data,
                ...(versionNote !== undefined && { version_note: versionNote }),
              }
            : v
        )
      );
    } catch (error: unknown) {
      const maskedUserId = user.id ? user.id.slice(0, 8) + '***' : 'unknown';

      const versionNameSafe = currentVersion?.version_name || versionName;
      const versionNameLength = versionNameSafe?.length || 0;
      const versionNameSafeFlag = versionNameSafe ? 'present' : 'missing';

      const dataType = Array.isArray(data) ? 'array' : typeof data;
      const dataSize = JSON.stringify(data).length;

      console.error('[useToolVersion] Error saving version:', {
        operation: 'save version',
        toolId,
        productId,
        toolLinkId,
        versionId,
        maskedUserId,
        dataType,
        dataSize,
        versionNameSafeFlag,
        versionNameLength,
        hasVersionNote: !!versionNote,
        versionNoteLength: versionNote?.length || 0,
        error: {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack:
            error instanceof Error ? error.stack : 'No stack trace available',
          name: error instanceof Error ? error.name : 'UnknownError',
          code: (error as { code?: string })?.code,
          details: (error as { details?: string })?.details,
          hint: (error as { hint?: string })?.hint,
        },
      });
      throw error;
    }
  };

  const createNewVersion = async (toolLinkId?: string) => {
    if (!user) return null;

    try {
      let countQuery = supabase
        .from('tool_versions')
        .select('id')
        .eq('tool_id', toolId)
        .eq('user_id', user.id);

      if (toolLinkId) {
        countQuery = countQuery.eq('product_tool_link_id', toolLinkId);
      }

      const { data: existingVersions } = await countQuery;
      const versionNumber = (existingVersions?.length || 0) + 1;

      const { data: newVersion, error } = await supabase
        .from('tool_versions')
        .insert({
          tool_id: toolId,
          version_name: `Version ${versionNumber}`,
          tool_data: {},
          user_id: user.id,
          product_tool_link_id: toolLinkId || null,
          original_product_id: productId || null,
        })
        .select()
        .single();

      if (error) throw error;

      const tool = AVAILABLE_TOOLS.find((t) => t.id === toolId);
      await trackEvent(PostHogEventName.UserFirstToolUsed, {
        tool_id: toolId,
        tool_name: tool?.name || toolId,
      });

      setVersions((prev) => [newVersion, ...prev]);
      setCurrentVersion(newVersion);

      return newVersion;
    } catch (error) {
      console.error('Error creating new version:', error);
      throw error;
    }
  };

  const deleteVersion = async (versionId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('tool_versions')
        .delete()
        .eq('id', versionId)
        .eq('user_id', user.id);

      if (error) throw error;

      await fetchVersions();
    } catch (error) {
      console.error('Error deleting version:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (isLoaded) {
      fetchVersions();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    toolId,
    productId,
    versionId,
    versionName,
    toolLinkId,
    isLoaded,
    user?.id,
  ]);

  return {
    currentVersion,
    versions,
    loading,
    saveVersion,
    createNewVersion,
    deleteVersion,
    refetch: fetchVersions,
  };
};
